import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  IconArrowRight,
  IconCheck,
  IconLock,
  IconZap,
  IconChevronRight,
  IconWifi,
  IconBuildingBank,
  IconDatabase,
} from './SVGIcons';
import PaymentsAPI from 'api/PaymentsAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import { Modal } from '@mui/material';
import CreatePaymentModal from 'components/Plans/CreatePaymentModal';
import {
  findStripeCustomerForTeam,
  resolveStripeCustomerId,
} from 'utils/stripeCustomer';
import { formatCreditPackPrice } from './CreditCalculator';

interface OrderItem {
  name: string;
  accent: string;
  qty: number;
  price: number;
}

interface CheckoutSubscriptionItem {
  priceId: string;
  quantity: number;
  moduleName: string;
  type:
    | 'fixed'
    | 'database_credit'
    | 'product_database_credit'
    | 'analyzers_credit'
    | 'workflows_credit'
    | 'business_verification_addon'
    | 'corporate_ownership_add_on'
    // Tier change on an already-active recurring pack (Database or
    // Workflows) -- confirmed here via PaymentsAPI.changePlan instead of
    // creating a new subscription.
    | 'database_credit_change'
    | 'workflows_credit_change'
    // Merges Database's two legacy subscriptions (Entity DB + Product DB)
    // into one at this tier -- confirmed here via
    // PaymentsAPI.mergeDatabaseCreditSubscriptions.
    | 'database_credit_merge';
  includedAnalysisCreditsMode?: 'one_time';
  // Only set for the *_change/*_merge types above.
  subId?: string;
  subItemId?: string;
  secondarySubId?: string;
  // Sent as subscription metadata; the invoice.paid webhook gives the buyer
  // this seat once the purchase is paid.
  autoAssignLicense?: 'standard' | 'pro' | 'business';
}

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  onPurchaseCompleted?: () => Promise<void> | void;
  orderItems: OrderItem[];
  totalCredits: number;
  creditPacks?: {
    type: CreditPackDisplayType;
    credits: number;
    price: number;
  }[];
  monthlyTotal: number;
  // Set when the cart contains tier changes on already-active packs: Stripe
  // bills only the prorated `dueToday` for them, not their share of
  // monthlyTotal (`monthlyFull`), so Pay must swap one for the other.
  tierChangeBilling?: { monthlyFull: number; dueToday: number };
  oneTimeTotal?: number;
  subscriptions: CheckoutSubscriptionItem[];
  handleTabChange: (tab: string) => void;
  // Prorated license upgrade picked alongside the packs. Applied with the same
  // payment method right before the packs are bought, so one Pay click charges
  // everything shown in "Due today".
  licenseUpgrade?: {
    dueToday: number;
    apply: (paymentMethodId: string) => Promise<boolean>;
  };
}

type CreditPackDisplayType =
  | 'database'
  | 'product_database'
  | 'analyzers'
  | 'workflows'
  | 'business_verification_addon'
  | 'corporate_ownership_add_on';

type Step = 'review' | 'payment' | 'confirmed';

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  open,
  onClose,
  onPurchaseCompleted,
  orderItems,
  totalCredits,
  creditPacks = [],
  monthlyTotal,
  tierChangeBilling,
  oneTimeTotal = 0,
  subscriptions,
  handleTabChange,
  licenseUpgrade,
}: CheckoutModalProps) => {
  const [step, setStep] = useState<Step>('review');
  const [createMode, setCreateMode] = useState(false);
  const [paymentSubmit, setPaymentSubmit] = useState('');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');

  const { cur_user } = useContext(GlobalContext);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  // A ref, not state: "Assign Licenses" finalizes and then closes in one click,
  // and both calls read the same render's state, so a state guard let
  // onPurchaseCompleted (trial cancellation included) run twice.
  const purchaseFinalizedRef = useRef(false);
  // licenseUpgrade clears once applied (the pending change is consumed), so keep
  // the amount for the confirmed receipt and any retry of the pack purchase.
  const [paidLicenseDue, setPaidLicenseDue] = useState(0);

  // Start from the login-time cache so there is no flash of "no customer", then
  // confirm against Stripe. cur_user.stripe_cus_info is captured once at login
  // and never refreshed, so a customer created mid-session — a new team, or a
  // team set up by hand — is otherwise invisible until the user signs in again,
  // and the payment modal fails with an unexplained error.
  const selectedTeamKey = String(
    cur_user?.selected_team?.id ?? cur_user?.selected_team?.name ?? '',
  );
  const selectedTeamName = cur_user?.selected_team?.name;

  const cachedCustomerId = useMemo(
    () =>
      String(
        findStripeCustomerForTeam(cur_user?.stripe_cus_info, selectedTeamName)
          ?.id ?? '',
      ),
    [cur_user?.stripe_cus_info, selectedTeamName],
  );

  // Keyed to the team it was resolved for, and ignored once that stops matching.
  // Holding a bare customer id meant switching teams left the *previous* team's
  // Stripe customer in state until the async resolve returned — and a purchase
  // in that window would have been charged to the wrong customer. Falling back
  // to the cached lookup keeps the "no flash of empty" behaviour.
  const [resolvedCustomer, setResolvedCustomer] = useState<{
    teamKey: string;
    id: string;
  }>({ teamKey: selectedTeamKey, id: cachedCustomerId });

  const customerId =
    resolvedCustomer.teamKey === selectedTeamKey
      ? resolvedCustomer.id
      : cachedCustomerId;

  useEffect(() => {
    let cancelled = false;
    resolveStripeCustomerId(
      cur_user?.stripe_cus_info,
      selectedTeamName,
      cur_user?.basics?.email,
      PaymentsAPI.getCustomerByEmail,
    ).then((resolved) => {
      if (!cancelled) {
        setResolvedCustomer({ teamKey: selectedTeamKey, id: resolved });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    cachedCustomerId,
    selectedTeamKey,
    selectedTeamName,
    cur_user?.stripe_cus_info,
    cur_user?.basics?.email,
  ]);

  const defaultPaymentMethod = useMemo(() => {
    if (!Array.isArray(paymentMethods)) {
      return null;
    }
    return (
      paymentMethods.find((method: any) => method?.isDefault) ||
      paymentMethods[0] ||
      null
    );
  }, [paymentMethods]);

  const selectedPaymentMethod = useMemo(() => {
    if (!Array.isArray(paymentMethods) || paymentMethods.length === 0) {
      return null;
    }

    return (
      paymentMethods.find(
        (method: any) => method?.id === selectedPaymentMethodId,
      ) || defaultPaymentMethod
    );
  }, [paymentMethods, selectedPaymentMethodId, defaultPaymentMethod]);

  const loadPaymentMethods = async (preferredPaymentMethodId?: string) => {
    if (!customerId) {
      return;
    }

    setPaymentMethodsLoading(true);
    setPaymentError('');
    const paymentMethodsResp = await PaymentsAPI.getCustomerPaymentMethods(
      customerId,
    );
    if (paymentMethodsResp?.success) {
      const methodsResult = paymentMethodsResp.result;
      const normalizedMethods = Array.isArray(methodsResult)
        ? methodsResult
        : Array.isArray(methodsResult?.data)
        ? methodsResult.data
        : [];
      setPaymentMethods(normalizedMethods);
      const nextSelectedPaymentMethodId =
        preferredPaymentMethodId ||
        selectedPaymentMethodId ||
        normalizedMethods[0]?.id ||
        '';
      setSelectedPaymentMethodId(nextSelectedPaymentMethodId);
    } else {
      setPaymentMethods([]);
      setPaymentError('Unable to load payment methods right now.');
    }
    setPaymentMethodsLoading(false);
  };

  useEffect(() => {
    if (!open || !customerId) {
      return;
    }

    loadPaymentMethods();
  }, [open, customerId]);

  useEffect(() => {
    if (!paymentSubmit || !open) {
      return;
    }

    setSelectedPaymentMethodId(paymentSubmit);
    loadPaymentMethods(paymentSubmit);
  }, [paymentSubmit, open]);

  const canPayWithSavedMethod =
    Boolean(selectedPaymentMethod?.id) && !paymentMethodsLoading;

  const parseCardBrand = (brand: string) => {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const getPaymentMethodDisplay = (paymentMethod: any) => {
    if (!paymentMethod) {
      return {
        icon: <IconWifi />,
        title: 'No payment method on file',
        subtitle: '',
      };
    }

    if (paymentMethod.us_bank_account) {
      const accountType = paymentMethod.us_bank_account?.account_type
        ? paymentMethod.us_bank_account.account_type
            .replace('_', ' ')
            .replace(/\b\w/g, (letter: string) => letter.toUpperCase())
        : 'Bank account';
      return {
        icon: <IconBuildingBank />,
        title: `${
          paymentMethod.us_bank_account?.bank_name || 'Bank account'
        } ending in ${paymentMethod.us_bank_account?.last4 || '••••'}`,
        subtitle: `${accountType} • ACH debit`,
      };
    }

    return {
      icon: <IconWifi />,
      title: `${parseCardBrand(paymentMethod.card?.brand)} ending in ${
        paymentMethod.card?.last4
      }`,
      subtitle: `Expires ${String(paymentMethod.card?.exp_month).padStart(
        2,
        '0',
      )}/${paymentMethod.card?.exp_year}`,
    };
  };

  const steps: { id: Step; label: string }[] = [
    { id: 'review', label: 'Review' },
    { id: 'payment', label: 'Payment' },
  ];
  const currentStepIndex = steps.findIndex((s) => s.id === step);

  const finalizePurchaseCompletion = async () => {
    if (step !== 'confirmed' || purchaseFinalizedRef.current) {
      return;
    }
    purchaseFinalizedRef.current = true;
    await onPurchaseCompleted?.();
  };

  const handleClose = async () => {
    await finalizePurchaseCompletion();
    onClose();
    setTimeout(() => {
      setStep('review');
      setCreateMode(false);
      setPaymentSubmit('');
      setSelectedPaymentMethodId('');
      setPaymentError('');
      setPurchaseLoading(false);
      purchaseFinalizedRef.current = false;
      setPaidLicenseDue(0);
    }, 300);
  };

  if (!open) return null;

  const selectedPaymentDisplay = getPaymentMethodDisplay(selectedPaymentMethod);
  const licenseDue = licenseUpgrade?.dueToday ?? paidLicenseDue;
  const tierChangeDue = tierChangeBilling?.dueToday ?? 0;
  const dueTodayTotal =
    monthlyTotal -
    (tierChangeBilling?.monthlyFull ?? 0) +
    tierChangeDue +
    oneTimeTotal +
    licenseDue;
  const hasMonthlyTotal = monthlyTotal > 0;
  const getCreditPackLabel = (type: CreditPackDisplayType) => {
    if (type === 'business_verification_addon') {
      return 'Business Verification add-on';
    }
    if (type === 'corporate_ownership_add_on') {
      return 'Corporate Ownership Check add-on';
    }
    if (type === 'analyzers') {
      return 'Analyzers';
    }
    if (type === 'workflows') {
      return 'Workflows';
    }
    if (type === 'product_database') {
      return 'Product database';
    }
    return 'Database';
  };
  // Usage-based add-ons have no upfront price or credit count.
  const isUsageBasedAddOn = (type: CreditPackDisplayType) =>
    type === 'business_verification_addon' ||
    type === 'corporate_ownership_add_on';
  const usageRateLabel = (type: CreditPackDisplayType) =>
    type === 'corporate_ownership_add_on'
      ? '$0.75 per ownership check'
      : '$0.50 per entity verified';
  // Recurring packs (billed /mo); everything else is a one-time purchase.
  const isRecurringCreditPackType = (type: CreditPackDisplayType) =>
    type !== 'analyzers';

  return (
    <div
      className='pricing-overlay'
      onClick={(e) => {
        if (e.target === e.currentTarget && !createMode) {
          handleClose();
        }
      }}
    >
      <div className='cm-modal' onClick={(e) => e.stopPropagation()}>
        {step !== 'confirmed' ? (
          <>
            <div className='pricing-modal-header'>
              <h3>Complete your order</h3>
              <div className='pricing-step-indicator'>
                {steps.map((s, i) => (
                  <div key={s.id} className='cm-step-group'>
                    <div
                      className={
                        i < currentStepIndex
                          ? 'cm-step-past'
                          : i === currentStepIndex
                          ? 'cm-step-current'
                          : 'cm-step-future'
                      }
                    >
                      <span
                        className='pricing-step-dot'
                        data-state={
                          i < currentStepIndex
                            ? 'completed'
                            : i === currentStepIndex
                            ? 'active'
                            : 'default'
                        }
                      >
                        {i < currentStepIndex ? <IconCheck /> : i + 1}
                      </span>
                      {s.label}
                    </div>
                    {i < steps.length - 1 && (
                      <span className='cm-step-chevron'>
                        <IconChevronRight />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className='checkout-layout'>
              <div className='checkout-main'>
                {step === 'review' && (
                  <div className='cm-review'>
                    <div className='cm-section-title'>Order summary</div>
                    <div className='cm-items'>
                      {orderItems.map((item) => (
                        <div key={item.name} className='cm-order-item'>
                          <div className='cm-order-item-left'>
                            <span
                              className='cm-order-dot'
                              style={{ background: item.accent }}
                            />
                            <span className='cm-order-name'>{item.name}</span>
                            <span className='cm-order-qty'>
                              × {item.qty}{' '}
                              {item.qty === 1 ? 'license' : 'licenses'}
                            </span>
                          </div>
                          <span className='cm-order-price'>
                            ${formatCreditPackPrice(item.price * item.qty)}/mo
                          </span>
                        </div>
                      ))}
                    </div>
                    {totalCredits > 0 && (
                      <div className='cm-credits-box'>
                        <div className='cm-credits-left'>
                          <IconZap />
                          <span className='cm-credits-label'>
                            Analyzer credits included
                          </span>
                        </div>
                        <span className='cm-credits-value'>
                          {totalCredits.toLocaleString()} credits
                        </span>
                      </div>
                    )}
                    {creditPacks.map((creditPack) => (
                      <div
                        className='cm-creditpack-box'
                        key={`${creditPack.type}-${creditPack.credits}`}
                      >
                        <div className='cm-creditpack-left'>
                          {isRecurringCreditPackType(creditPack.type) ? (
                            <IconDatabase />
                          ) : (
                            <IconZap />
                          )}
                          <span className='cm-creditpack-label'>
                            {getCreditPackLabel(creditPack.type)}
                          </span>
                          {!isUsageBasedAddOn(creditPack.type) && (
                            <span className='cm-creditpack-detail'>
                              ({creditPack.credits.toLocaleString()} credits)
                            </span>
                          )}
                        </div>
                        <span className='cm-creditpack-price'>
                          {isUsageBasedAddOn(creditPack.type)
                            ? usageRateLabel(creditPack.type)
                            : `+$${formatCreditPackPrice(creditPack.price)}${
                                isRecurringCreditPackType(creditPack.type)
                                  ? '/mo'
                                  : ' once'
                              }`}
                        </span>
                      </div>
                    ))}
                    {licenseDue > 0 && (
                      <div className='cm-creditpack-box'>
                        <div className='cm-creditpack-left'>
                          <span className='cm-creditpack-label'>
                            License upgrade (prorated)
                          </span>
                        </div>
                        <span className='cm-creditpack-price'>
                          +${formatCreditPackPrice(licenseDue)} today
                        </span>
                      </div>
                    )}
                    <div className='cm-review-footer'>
                      <div>
                        <div className='cm-billed-label'>
                          {hasMonthlyTotal ? 'Billed monthly' : 'One-time'}
                        </div>
                        {hasMonthlyTotal && (
                          <div className='cm-grand-total'>
                            ${formatCreditPackPrice(monthlyTotal)}/mo
                          </div>
                        )}
                        {oneTimeTotal > 0 && (
                          <div className='cm-grand-total'>
                            {hasMonthlyTotal ? '+' : ''}$
                            {formatCreditPackPrice(oneTimeTotal)}
                          </div>
                        )}
                      </div>
                      <button
                        className='cm-continue-btn'
                        onClick={() => setStep('payment')}
                      >
                        Continue <IconArrowRight />
                      </button>
                    </div>
                    {purchaseLoading && (
                      <div className='cm-pay-loading'>
                        Finalizing your purchase…
                      </div>
                    )}
                  </div>
                )}
                {step === 'payment' && (
                  <div className='cm-payment'>
                    <div className='cm-payment-header'>
                      <div className='cm-section-title'>Payment details</div>
                      <div className='cm-stripe-badge'>
                        <IconLock /> Secured by Stripe
                      </div>
                    </div>
                    <button
                      type='button'
                      onClick={() => {
                        if (selectedPaymentMethod?.id) {
                          setSelectedPaymentMethodId(selectedPaymentMethod.id);
                        }
                      }}
                      className='pricing-radio-option'
                      data-selected={canPayWithSavedMethod || undefined}
                      disabled={!canPayWithSavedMethod}
                    >
                      <div
                        className='pricing-radio-dot'
                        data-selected={canPayWithSavedMethod || undefined}
                      >
                        {canPayWithSavedMethod && (
                          <span className='pricing-radio-dot-inner' />
                        )}
                      </div>
                      <div className='cm-saved-card'>
                        <div className='cm-card-icon'>
                          {selectedPaymentDisplay.icon}
                        </div>
                        <div>
                          {paymentMethodsLoading ? (
                            <>
                              <div className='cm-card-name'>
                                Loading payment method…
                              </div>
                              <div className='cm-card-expiry'>Please wait</div>
                            </>
                          ) : selectedPaymentMethod ? (
                            <>
                              <div className='cm-card-name'>
                                {selectedPaymentDisplay.title}
                              </div>
                              <div className='cm-card-expiry'>
                                {selectedPaymentDisplay.subtitle}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className='cm-card-name'>
                                No payment method on file
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      {selectedPaymentMethod && (
                        <span className='cm-default-badge'>Selected</span>
                      )}
                    </button>
                    {paymentError && (
                      <p className='cm-payment-method-empty'>{paymentError}</p>
                    )}
                    {!paymentMethodsLoading &&
                      !selectedPaymentMethod &&
                      !paymentError && (
                        <div className='cm-payment-method-empty'>
                          You need a payment method before checking out.
                        </div>
                      )}
                    <button
                      type='button'
                      className='cm-add-payment-btn'
                      onClick={() => setCreateMode(true)}
                    >
                      Add payment method
                    </button>
                    <div className='cm-payment-footer'>
                      <button
                        className='cm-back-btn'
                        onClick={() => setStep('review')}
                      >
                        Back
                      </button>
                      <button
                        className='cm-pay-btn'
                        onClick={async () => {
                          if (
                            !customerId ||
                            !selectedPaymentMethod?.id ||
                            purchaseLoading
                          ) {
                            return;
                          }
                          setPurchaseLoading(true);
                          setPaymentError('');
                          if (licenseUpgrade) {
                            let licenseApplied = false;
                            try {
                              licenseApplied = await licenseUpgrade.apply(
                                selectedPaymentMethod.id,
                              );
                            } catch {
                              licenseApplied = false;
                            }
                            if (!licenseApplied) {
                              setPurchaseLoading(false);
                              setPaymentError(
                                'Your license upgrade could not be completed, so nothing was charged. Please try again.',
                              );
                              return;
                            }
                            setPaidLicenseDue(licenseUpgrade.dueToday);
                          }
                          const recurringSubscriptions = subscriptions.filter(
                            (subscription) =>
                              subscription.type === 'fixed' ||
                              subscription.type === 'database_credit' ||
                              subscription.type === 'product_database_credit' ||
                              subscription.type === 'workflows_credit' ||
                              subscription.type ===
                                'business_verification_addon' ||
                              subscription.type ===
                                'corporate_ownership_add_on',
                          );
                          const analysisCreditPurchases = subscriptions.filter(
                            (subscription) =>
                              subscription.type === 'analyzers_credit',
                          );
                          // Tier change on an already-active pack -- update the
                          // existing subscription item's price in place rather
                          // than creating a second, overlapping subscription.
                          const tierChangeSubscriptions = subscriptions.filter(
                            (subscription) =>
                              subscription.type === 'database_credit_change' ||
                              subscription.type === 'workflows_credit_change',
                          );
                          // Merges Database's two legacy subscriptions into one.
                          const mergeSubscriptions = subscriptions.filter(
                            (subscription) =>
                              subscription.type === 'database_credit_merge',
                          );

                          const subscriptionPayload =
                            recurringSubscriptions.map((subscription) => ({
                              lineItems: [
                                {
                                  price: subscription.priceId,
                                  // Business Verification is a metered
                                  // (usage-based) price: Stripe rejects a
                                  // quantity on those.
                                  quantity:
                                    subscription.type ===
                                      'business_verification_addon' ||
                                    subscription.type ===
                                      'corporate_ownership_add_on'
                                      ? undefined
                                      : subscription.type ===
                                          'database_credit' ||
                                        subscription.type ===
                                          'product_database_credit' ||
                                        subscription.type === 'workflows_credit'
                                      ? 1
                                      : subscription.quantity,
                                  metadata: {
                                    type: subscription.type,
                                    moduleName: subscription.moduleName,
                                    included_analysis_credits_mode: 'one_time',
                                  },
                                },
                              ],
                              metadata: {
                                freeTrial: false,
                                freeTrialDays: 0,
                                team_id: cur_user?.selected_team?.id,
                                keycloakUserId: cur_user?.basics?.id,
                                userName:
                                  [
                                    cur_user?.basics?.firstName,
                                    cur_user?.basics?.lastName,
                                  ]
                                    .filter(Boolean)
                                    .join(' ') || undefined,
                                userEmail: cur_user?.basics?.email,
                                type: subscription.type,
                                moduleName: subscription.moduleName,
                                included_analysis_credits_mode:
                                  subscription.includedAnalysisCreditsMode ||
                                  'one_time',
                                ...(subscription.autoAssignLicense
                                  ? {
                                      autoAssignLicense:
                                        subscription.autoAssignLicense,
                                    }
                                  : {}),
                              },
                            }));

                          const checkoutActions: Array<Promise<any>> = [];
                          if (subscriptionPayload.length > 0) {
                            checkoutActions.push(
                              PaymentsAPI.createMultipleSubscriptions(
                                customerId,
                                selectedPaymentMethod.id,
                                subscriptionPayload,
                              ),
                            );
                          }
                          analysisCreditPurchases.forEach((purchase) => {
                            checkoutActions.push(
                              PaymentsAPI.createPaymentIntent(
                                customerId,
                                purchase.priceId,
                                selectedPaymentMethod.id,
                                {
                                  team_id: cur_user?.selected_team?.id,
                                  teamId: cur_user?.selected_team?.id,
                                  keycloakUserId: cur_user?.basics?.id,
                                  userName:
                                    [
                                      cur_user?.basics?.firstName,
                                      cur_user?.basics?.lastName,
                                    ]
                                      .filter(Boolean)
                                      .join(' ') || undefined,
                                  userEmail: cur_user?.basics?.email,
                                },
                              ),
                            );
                          });
                          tierChangeSubscriptions.forEach((change) => {
                            if (!change.subId || !change.subItemId) {
                              return;
                            }
                            checkoutActions.push(
                              PaymentsAPI.changePlan({
                                sub_id: change.subId,
                                subItemId: change.subItemId,
                                newPrice: change.priceId,
                                newQuantity: 1,
                                metadata: { moduleName: change.moduleName },
                              }),
                            );
                          });
                          mergeSubscriptions.forEach((merge) => {
                            if (
                              !merge.subId ||
                              !merge.subItemId ||
                              !merge.secondarySubId
                            ) {
                              return;
                            }
                            checkoutActions.push(
                              PaymentsAPI.mergeDatabaseCreditSubscriptions({
                                primarySubId: merge.subId,
                                primarySubItemId: merge.subItemId,
                                secondarySubId: merge.secondarySubId,
                                newPriceId: merge.priceId,
                                moduleName: merge.moduleName,
                              }),
                            );
                          });

                          // A selected pack whose Stripe price id hasn't
                          // resolved never becomes a line item; without this,
                          // Promise.all([]) reads as success and shows a
                          // purchase confirmation with nothing charged.
                          if (checkoutActions.length === 0) {
                            setPurchaseLoading(false);
                            setPaymentError(
                              'One or more selected packs are unavailable right now, so nothing was purchased. Please refresh and try again.',
                            );
                            return;
                          }

                          const checkoutResponses = await Promise.all(
                            checkoutActions,
                          );
                          const failedResponse = checkoutResponses.find(
                            (response) => !response?.success,
                          );
                          setPurchaseLoading(false);
                          if (!failedResponse) {
                            setStep('confirmed');
                          } else {
                            setPaymentError(
                              failedResponse?.result ||
                                'Unable to complete checkout. Please try again.',
                            );
                          }
                        }}
                        disabled={purchaseLoading || !canPayWithSavedMethod}
                      >
                        {purchaseLoading
                          ? 'Processing…'
                          : `Pay $${formatCreditPackPrice(dueTodayTotal)}${
                              oneTimeTotal > 0 ||
                              licenseDue > 0 ||
                              tierChangeBilling
                                ? ' today'
                                : '/mo'
                            }`}{' '}
                        <IconLock />
                      </button>
                    </div>
                    {purchaseLoading && (
                      <p className='cm-pay-loading'>
                        Finalizing your purchase…
                      </p>
                    )}
                  </div>
                )}
              </div>
              {step !== 'review' && (
                <div className='checkout-sidebar'>
                  <p className='cm-sidebar-title'>Your order</p>
                  {orderItems.map((item) => (
                    <div key={item.name} className='cm-sidebar-item'>
                      <span
                        className='cm-sidebar-dot'
                        style={{ background: item.accent }}
                      />
                      <span className='cm-sidebar-name'>{item.name}</span>
                      <span className='cm-sidebar-qty'>×{item.qty}</span>
                    </div>
                  ))}
                  {creditPacks.map((creditPack) => (
                    <div
                      className='cm-sidebar-credit'
                      key={`sidebar-${creditPack.type}-${creditPack.credits}`}
                    >
                      {isRecurringCreditPackType(creditPack.type) ? (
                        <IconDatabase />
                      ) : (
                        <IconZap />
                      )}
                      <span className='cm-sidebar-credit-label'>
                        {getCreditPackLabel(creditPack.type)}
                        {!isUsageBasedAddOn(creditPack.type) &&
                          ` ${creditPack.credits.toLocaleString()} credits`}
                      </span>
                    </div>
                  ))}
                  <hr className='pricing-separator' />
                  {hasMonthlyTotal && (
                    <div className='cm-sidebar-total'>
                      <span className='cm-sidebar-total-label'>
                        Monthly total
                      </span>
                      <span className='cm-sidebar-total-value'>
                        ${formatCreditPackPrice(monthlyTotal)}/mo
                      </span>
                    </div>
                  )}
                  {oneTimeTotal > 0 && (
                    <div className='cm-sidebar-total'>
                      <span className='cm-sidebar-total-label'>One-time</span>
                      <span className='cm-sidebar-total-value'>
                        {hasMonthlyTotal ? '+' : ''}$
                        {formatCreditPackPrice(oneTimeTotal)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className='cm-confirmed'>
            <div className='cm-confirmed-icon'>
              <IconCheck />
            </div>
            <h2 className='cm-confirmed-title'>You&apos;re all set!</h2>
            <p className='cm-confirmed-text'>Your subscription is confirmed.</p>
            <p className='cm-confirmed-subtext'>
              If you have additional team members who need access you can assign
              remaining licenses to them now.
            </p>
            <div className='cm-confirmed-receipt'>
              {orderItems.map((item) => (
                <div key={item.name} className='cm-confirmed-item'>
                  <div className='cm-confirmed-item-left'>
                    <span
                      className='cm-confirmed-dot'
                      style={{ background: item.accent }}
                    />
                    <span>
                      {item.name} × {item.qty}
                    </span>
                  </div>
                  <span className='cm-confirmed-item-price'>
                    ${formatCreditPackPrice(item.price * item.qty)}/mo
                  </span>
                </div>
              ))}
              {creditPacks.map((creditPack) => (
                <div
                  className='cm-confirmed-item'
                  key={`confirmed-${creditPack.type}-${creditPack.credits}`}
                >
                  <div className='cm-confirmed-item-left'>
                    <span className='cm-confirmed-zap'>
                      {isRecurringCreditPackType(creditPack.type) ? (
                        <IconDatabase />
                      ) : (
                        <IconZap />
                      )}
                    </span>
                    <span>
                      {getCreditPackLabel(creditPack.type)} credit pack (
                      {creditPack.credits.toLocaleString()})
                    </span>
                  </div>
                  <span className='cm-confirmed-item-price'>
                    {isUsageBasedAddOn(creditPack.type)
                      ? usageRateLabel(creditPack.type)
                      : `+$${formatCreditPackPrice(creditPack.price)}${
                          isRecurringCreditPackType(creditPack.type)
                            ? '/mo'
                            : ' once'
                        }`}
                  </span>
                </div>
              ))}
              {licenseDue > 0 && (
                <div className='cm-confirmed-item'>
                  <div className='cm-confirmed-item-left'>
                    <span>License upgrade (prorated)</span>
                  </div>
                  <span className='cm-confirmed-item-price'>
                    ${formatCreditPackPrice(licenseDue)}
                  </span>
                </div>
              )}
              <hr className='pricing-separator' />
              {hasMonthlyTotal && (
                <div className='cm-confirmed-total'>
                  <span>Monthly total</span>
                  <span>${formatCreditPackPrice(monthlyTotal)}/mo</span>
                </div>
              )}
              {oneTimeTotal > 0 && (
                <div className='cm-confirmed-total'>
                  <span>One-time</span>
                  <span>+${formatCreditPackPrice(oneTimeTotal)}</span>
                </div>
              )}
            </div>
            <button
              className='cm-dashboard-btn'
              onClick={async () => {
                await finalizePurchaseCompletion();
                handleTabChange('Assign');
                await handleClose();
              }}
            >
              Assign Licenses <IconArrowRight />
            </button>
          </div>
        )}
      </div>

      <Modal open={createMode} onClose={() => setCreateMode(false)}>
        <CreatePaymentModal
          cusId={customerId}
          setCreateMode={setCreateMode}
          paymentSubmit={paymentSubmit}
          setPaymentSubmit={setPaymentSubmit}
        />
      </Modal>
    </div>
  );
};

export default CheckoutModal;
